import React from 'react'
import { colors, radius, fontFamily, fontSize, fontWeight, componentHeight, transition } from '../../styles/theme'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: React.ReactNode
  iconRight?: React.ReactNode
}

const variantStyles: Record<Variant, React.CSSProperties> = {
  primary: {
    background: colors.primary,
    color: '#fff',
    border: '1px solid transparent',
  },
  secondary: {
    background: 'transparent',
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
  },
  ghost: {
    background: 'transparent',
    color: colors.textSecondary,
    border: '1px solid transparent',
  },
  danger: {
    background: 'transparent',
    color: colors.error,
    border: `1px solid ${colors.errorBorder}`,
  },
}

const variantHover: Record<Variant, React.CSSProperties> = {
  primary:   { background: colors.primaryHover },
  secondary: { background: colors.primaryLight },
  ghost:     { background: colors.surfaceMist ?? '#F6FAF9', color: colors.text },
  danger:    { background: colors.errorLight },
}

const sizeStyles: Record<Size, React.CSSProperties> = {
  sm: { height: componentHeight.btnSm, padding: '0 12px', fontSize: fontSize.xs, gap: 6 },
  md: { height: componentHeight.btnMd, padding: '0 16px', fontSize: fontSize.sm, gap: 7 },
  lg: { height: componentHeight.btnLg, padding: '0 20px', fontSize: fontSize.base, gap: 8 },
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconRight,
  children,
  disabled,
  style,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: ButtonProps) {
  const [hovered, setHovered] = React.useState(false)

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.full,
    fontFamily: fontFamily.sans,
    fontWeight: fontWeight.medium,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: `background ${transition.fast}, color ${transition.fast}, border-color ${transition.fast}`,
    letterSpacing: '-0.01em',
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...(hovered && !disabled && !loading ? variantHover[variant] : {}),
    ...style,
  }

  return (
    <button
      disabled={disabled || loading}
      style={base}
      onMouseEnter={e => { setHovered(true); onMouseEnter?.(e) }}
      onMouseLeave={e => { setHovered(false); onMouseLeave?.(e) }}
      {...rest}
    >
      {loading ? <Spinner size={size} /> : icon}
      {children}
      {!loading && iconRight}
    </button>
  )
}

function Spinner({ size }: { size: Size }) {
  const s = size === 'sm' ? 12 : size === 'lg' ? 16 : 14
  return (
    <span style={{
      display: 'block',
      width: s,
      height: s,
      border: '2px solid currentColor',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'docm-spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  )
}
